import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../utils/api';

interface Tournament {
  id: string;
  name: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  format?: string;
}

interface Player {
  id: string; // Для нескольких регистраций: "playerId-membershipId", иначе просто "playerId"
  playerId?: string; // Оригинальный ID игрока (если id составной)
  membershipId?: string; // ID регистрации (если есть несколько регистраций)
  name: string;
  isTeam?: boolean;
  player1Id?: string;
  player2Id?: string;
  player1?: Player;
  player2?: Player;
}

interface BracketTemplate {
  id: string;
  name: string;
  format: string;
  playerCount: number;
  structure: string;
  currentStage?: number;
}

interface DrawEntry {
  id: string;
  position: string;
  playerId?: string;
  player1Id?: string;
  player2Id?: string;
  seed?: number;
  bracketTemplateId?: string;
  groupId?: string;
  player?: Player;
  player1?: Player;
  player2?: Player;
  template?: BracketTemplate;
  group?: {
    id: string;
    name: string;
  };
  // Для плей-офф: хранит место из группы (например, "A-1", "B-2")
  groupPlace?: string;
}

interface PlayoffStage {
  qualifiedFromGroups: number;
  playerCount: number;
  rounds: Array<{
    round: number;
    name: string;
    matches: number;
  }>;
  directFinal?: boolean; // Прямой выход в финал без промежуточных раундов
  thirdPlaceMatch?: boolean; // Игра за 3-е место
}

interface TournamentLevel {
  level: string;
  playerCount: number;
}

interface LevelMembership {
  playerId: string;
  player?: {
    id: string;
    name: string;
  };
}

const Draws: React.FC = () => {
  const DRAWS_SELECTION_STORAGE_KEY = 'draws-selection-v1';
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [levels, setLevels] = useState<TournamentLevel[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [selectionRestored, setSelectionRestored] = useState(false);
  const [levelRestored, setLevelRestored] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [drawEntries, setDrawEntries] = useState<DrawEntry[]>([]);
  const [bracketTemplate, setBracketTemplate] = useState<BracketTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);
  const [draggedPosition, setDraggedPosition] = useState<string | null>(null);
  const [groupNames, setGroupNames] = useState<Record<number, string>>({}); // { groupIndex: "Group A" }
  const [playoffStage, setPlayoffStage] = useState<PlayoffStage | null>(null);
  const [draggedGroupPlace, setDraggedGroupPlace] = useState<string | null>(null); // Для перетаскивания мест из групп (A-1, B-2)
  const [groupIdsByIndex, setGroupIdsByIndex] = useState<Record<number, string>>({});
  const [replacementGroupIndex, setReplacementGroupIndex] = useState<number | null>(null);
  const [replacementFromPlayerId, setReplacementFromPlayerId] = useState<string>('');
  const [replacementToPlayerId, setReplacementToPlayerId] = useState<string>('');
  const [replacementMode, setReplacementMode] = useState<'STANDARD' | 'REPLAY_ALL_GAMES'>('STANDARD');
  const [replacementCandidates, setReplacementCandidates] = useState<Array<{ id: string; name: string }>>([]);
  const [replacingMember, setReplacingMember] = useState(false);
  const tournamentStatusMeta: Record<string, { order: number; label: string }> = {
    ACTIVE: { order: 0, label: 'Активные' },
    PLANNED: { order: 1, label: 'Запланированные' },
    COMPLETED: { order: 2, label: 'Завершенные' },
    CANCELLED: { order: 3, label: 'Отмененные' }
  };
  const tournamentsGrouped = useMemo(() => {
    const toDateTs = (value?: string): number => {
      if (!value) return Number.NEGATIVE_INFINITY;
      const parsed = new Date(value).getTime();
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
    };

    const groups = new Map<string, { label: string; order: number; items: Tournament[] }>();
    for (const tournament of tournaments) {
      const statusKey = String(tournament.status || '').trim().toUpperCase();
      const statusInfo = tournamentStatusMeta[statusKey] || { order: 99, label: 'Прочие' };
      if (!groups.has(statusInfo.label)) {
        groups.set(statusInfo.label, { label: statusInfo.label, order: statusInfo.order, items: [] });
      }
      groups.get(statusInfo.label)!.items.push(tournament);
    }

    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ru'));
    for (const group of sortedGroups) {
      group.items.sort((a, b) => {
        const aTs = Math.max(toDateTs(a.endDate), toDateTs(a.startDate));
        const bTs = Math.max(toDateTs(b.endDate), toDateTs(b.startDate));
        if (aTs !== bTs) return bTs - aTs;
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
      });
    }
    return sortedGroups;
  }, [tournaments]);

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    if (selectionRestored || tournaments.length === 0) return;
    try {
      const raw = localStorage.getItem(DRAWS_SELECTION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { tournamentId?: string };
      if (!parsed?.tournamentId) return;
      const savedTournament = tournaments.find(t => t.id === parsed.tournamentId);
      if (savedTournament) {
        setSelectedTournament(savedTournament);
      }
    } catch (error) {
      console.error('Ошибка восстановления выбранного турнира (draws):', error);
    } finally {
      setSelectionRestored(true);
    }
  }, [tournaments, selectionRestored]);

  useEffect(() => {
    if (selectedTournament) {
      fetchLevels();
    }
  }, [selectedTournament]);

  useEffect(() => {
    setLevelRestored(false);
  }, [selectedTournament?.id]);

  useEffect(() => {
    if (!selectedTournament || levels.length === 0 || levelRestored) return;
    if (selectedLevel && levels.some((lvl) => lvl.level === selectedLevel)) {
      setLevelRestored(true);
      return;
    }

    try {
      const raw = localStorage.getItem(DRAWS_SELECTION_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) as { tournamentId?: string; level?: string } : null;
      const savedLevel =
        parsed?.tournamentId === selectedTournament.id
          ? (parsed?.level || '')
          : '';
      if (savedLevel && levels.some((lvl) => lvl.level === savedLevel)) {
        setSelectedLevel(savedLevel);
        setLevelRestored(true);
        return;
      }
    } catch (error) {
      console.error('Ошибка восстановления выбранного уровня (draws):', error);
    }

    setSelectedLevel(levels[0].level);
    setLevelRestored(true);
  }, [levels, selectedTournament, selectedLevel, levelRestored]);

  useEffect(() => {
    if (!selectionRestored) return;
    if (selectedTournament && !levelRestored) return;
    try {
      localStorage.setItem(
        DRAWS_SELECTION_STORAGE_KEY,
        JSON.stringify({
          tournamentId: selectedTournament?.id || '',
          level: selectedLevel || ''
        })
      );
    } catch (error) {
      console.error('Ошибка сохранения выбора жеребьевки:', error);
    }
  }, [selectedTournament, selectedLevel, selectionRestored, levelRestored]);

  useEffect(() => {
    if (!selectedTournament && selectionRestored) {
      try {
        localStorage.setItem(
          DRAWS_SELECTION_STORAGE_KEY,
          JSON.stringify({
            tournamentId: '',
            level: ''
          })
        );
      } catch (error) {
        console.error('Ошибка очистки выбора жеребьевки:', error);
      }
      return;
    }
  }, [selectedTournament, selectionRestored]);

  useEffect(() => {
    if (selectedTournament && selectedLevel) {
      fetchPlayers();
      fetchBracketTemplate();
    }
  }, [selectedTournament, selectedLevel]);

  useEffect(() => {
    if (selectedTournament && selectedLevel && bracketTemplate) {
      fetchDraw();
      fetchPlayoffStage();
      // Инициализируем названия групп по умолчанию
      const structure = getGroupStructure();
      if (structure && Object.keys(groupNames).length === 0) {
        const names: Record<number, string> = {};
        for (let i = 0; i < structure.groups; i++) {
          names[i] = `Группа ${String.fromCharCode(65 + i)}`; // A, B, C...
        }
        setGroupNames(names);
      }
    }
  }, [selectedTournament, selectedLevel, bracketTemplate]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const data = await apiGet<{ tournaments: Tournament[] } | Tournament[]>('/tournaments?limit=1000');
      // API возвращает объект с полем tournaments или массив
      if (Array.isArray(data)) {
        setTournaments(data);
      } else {
        setTournaments(Array.isArray(data.tournaments) ? data.tournaments : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки турниров:', error);
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLevels = async () => {
    if (!selectedTournament) return;
    try {
      const tournament = await apiGet<any>(`/tournaments/${selectedTournament.id}`);
      const memberships = tournament.memberships || [];
      const levelMap = new Map<string, number>();
      
      const isDoubles = tournament.format === 'DOUBLES';
      
      if (isDoubles) {
        // Для парных турниров считаем команды (пары)
        const teamsByLevel = new Map<string, Set<string>>();
        
        memberships.forEach((m: any) => {
          const level = m.level;
          if (level) {
            if (!teamsByLevel.has(level)) {
              teamsByLevel.set(level, new Set());
            }
            
            // Если у membership есть doublesTeamId, это команда
            if (m.doublesTeamId) {
              teamsByLevel.get(level)!.add(m.doublesTeamId);
            } else {
              // Если нет doublesTeamId, это игрок без пары - считаем как отдельную "команду" из одного игрока
              teamsByLevel.get(level)!.add(`solo_${m.playerId}`);
            }
          }
        });
        
        // Подсчитываем количество уникальных команд для каждого уровня
        teamsByLevel.forEach((teams, level) => {
          levelMap.set(level, teams.size);
        });
      } else {
        // Для одиночных турниров считаем отдельных игроков
        memberships.forEach((m: any) => {
          if (m.level) {
            levelMap.set(m.level, (levelMap.get(m.level) || 0) + 1);
          }
        });
      }

      const levelsList: TournamentLevel[] = Array.from(levelMap.entries())
        .map(([level, count]) => ({ level, playerCount: count }))
        .sort((a, b) => a.level.localeCompare(b.level));

      setLevels(levelsList);
    } catch (error) {
      console.error('Ошибка загрузки уровней:', error);
    }
  };

  const fetchPlayers = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      const data = await apiGet<Player[]>(`/draws/tournament/${selectedTournament.id}/players/${selectedLevel}`);
      setPlayers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки игроков:', error);
    }
  };

  const fetchDraw = async () => {
    if (!selectedTournament || !selectedLevel || !bracketTemplate) return;
    try {
      setLoading(true);
      const data = await apiGet<DrawEntry[]>(`/draws/tournament/${selectedTournament.id}/level/${selectedLevel}`);
      setDrawEntries(Array.isArray(data) ? data : []);
      
      // Загружаем названия групп из записей
      const names: Record<number, string> = {};
      const ids: Record<number, string> = {};
      const structure = getGroupStructure();
      if (structure) {
        // Инициализируем названия по умолчанию
        for (let i = 0; i < structure.groups; i++) {
          names[i] = `Группа ${String.fromCharCode(65 + i)}`;
        }
        
        // Загружаем группы из базы данных для этого уровня
        try {
          const groupsResponse = await apiGet<any[]>(`/tournament-groups/tournament/${selectedTournament.id}?level=${selectedLevel}`);
          if (Array.isArray(groupsResponse)) {
            groupsResponse.forEach((group: any) => {
              if (group.order !== null && group.order !== undefined) {
                names[group.order] = group.name;
                if (group.id) {
                  ids[group.order] = group.id;
                }
              }
            });
          }
        } catch (error) {
          console.error('Ошибка загрузки групп:', error);
          // Если не удалось загрузить группы, используем названия из записей
          const groupMap = new Map<string, { name: string; index: number }>();
          data.forEach((entry: DrawEntry) => {
            if (entry.group?.id && entry.group?.name) {
              const positionNum = parseInt(entry.position);
              if (!isNaN(positionNum)) {
                const groupIndex = Math.floor((positionNum - 1) / structure.playersPerGroup);
                groupMap.set(entry.group.id, { name: entry.group.name, index: groupIndex });
              }
            }
          });
          groupMap.forEach(({ name, index }) => {
            names[index] = name;
          });
        }
      }
      setGroupNames(names);
      setGroupIdsByIndex(ids);
    } catch (error) {
      console.error('Ошибка загрузки жеребьёвки:', error);
      setDrawEntries([]);
      setGroupNames({});
      setGroupIdsByIndex({});
    } finally {
      setLoading(false);
    }
  };

  const fetchBracketTemplate = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      const tournament = await apiGet<any>(`/tournaments/${selectedTournament.id}`);
      let levelBrackets: Record<string, any> = {};
      
      if (tournament.levelBrackets) {
        try {
          levelBrackets = typeof tournament.levelBrackets === 'string'
            ? JSON.parse(tournament.levelBrackets)
            : tournament.levelBrackets;
        } catch (e) {
          console.error('Ошибка парсинга levelBrackets:', e);
        }
      }

      const levelData = levelBrackets[selectedLevel];
      if (!levelData) {
        setBracketTemplate(null);
        return;
      }

      // Поддерживаем старый формат (просто templateId) и новый формат (объект)
      const templateId = typeof levelData === 'string' ? levelData : levelData.templateId;
      if (!templateId) {
        setBracketTemplate(null);
        return;
      }

      const template = await apiGet<BracketTemplate>(`/bracket-templates/${templateId}`);
      setBracketTemplate(template);
    } catch (error: any) {
      console.error('Ошибка загрузки шаблона сетки:', error);
      // Если шаблон не найден (404), просто не устанавливаем его
      if (error?.response?.status !== 404) {
        console.error('Неожиданная ошибка при загрузке шаблона:', error);
      }
      setBracketTemplate(null);
    }
  };

  const fetchPlayoffStage = async () => {
    if (!bracketTemplate || bracketTemplate.format !== 'MIXED') {
      setPlayoffStage(null);
      return;
    }

    try {
      const structure = typeof bracketTemplate.structure === 'string'
        ? JSON.parse(bracketTemplate.structure)
        : bracketTemplate.structure;

      if (structure.stages) {
        const playoffStageData = structure.stages.find((s: any) => s.type === 'SINGLE_ELIMINATION');
        if (playoffStageData) {
          // Рассчитываем playerCount для проверки корректности раундов
          const roundRobinStage = structure.stages.find((s: any) => s.type === 'ROUND_ROBIN');
          const groupsCount = roundRobinStage?.groups || 1;
          const qualifiedPerGroup = playoffStageData.qualifiedFromGroups || 0;
          const calculatedPlayerCount = qualifiedPerGroup * groupsCount;
          const finalPlayerCount = playoffStageData.playerCount || calculatedPlayerCount;
          
          // Получаем структуру раундов для Single Elimination
          let rounds: Array<{ round: number; name: string; matches: number }> = [];
          if (playoffStageData.templateId) {
            try {
              const playoffTemplate = await apiGet<BracketTemplate>(`/bracket-templates/${playoffStageData.templateId}`);
              const playoffStructure = typeof playoffTemplate.structure === 'string'
                ? JSON.parse(playoffTemplate.structure)
                : playoffTemplate.structure;
              rounds = playoffStructure.rounds || [];
              
              // Проверяем корректность раундов на основе playerCount
              // Если раунды не соответствуют playerCount, перегенерируем их
              const expectedRoundsCount = Math.ceil(Math.log2(finalPlayerCount));
              const firstRoundMatches = Math.pow(2, expectedRoundsCount - 1);
              if (rounds.length > 0) {
                const firstRound = rounds[rounds.length - 1];
                if (firstRound.matches !== firstRoundMatches) {
                  console.warn('Раунды из шаблона не соответствуют playerCount, перегенерируем:', {
                    templateMatches: firstRound.matches,
                    expectedMatches: firstRoundMatches,
                    playerCount: finalPlayerCount
                  });
                  rounds = []; // Сбрасываем, чтобы перегенерировать
                }
              }
            } catch (error) {
              console.error('Ошибка загрузки шаблона плей-офф:', error);
            }
          }

          // Если раунды не были загружены из шаблона или некорректны, генерируем их
          if (rounds.length === 0) {
            // НОВАЯ ЛОГИКА: Проверяем режим прямого финала
            if (playoffStageData.directFinal === true) {
              // Режим прямого финала: места из группы напрямую определяют финальные матчи
              let roundNumber = 1;
              
              // Если включена игра за 3-е место, добавляем её первой
              if (playoffStageData.thirdPlaceMatch === true) {
                rounds.push({
                  round: roundNumber,
                  name: 'Матч за 3-е место',
                  matches: 1
                });
                roundNumber++;
              }
              
              // Финал всегда есть
              rounds.push({
                round: roundNumber,
                name: 'Финал',
                matches: 1
              });
              
            } else {
              // Классический плей-офф с промежуточными раундами
              const roundsCount = Math.ceil(Math.log2(finalPlayerCount));
              for (let i = roundsCount; i > 0; i--) {
                const matchesInRound = Math.pow(2, i - 1);
                let roundName = '';
                
                // Определяем имя раунда в зависимости от количества матчей
                if (matchesInRound === 1) {
                  roundName = 'Финал';
                } else if (matchesInRound === 2) {
                  roundName = 'Полуфинал';
                } else if (matchesInRound === 4) {
                  roundName = '1/4 финала';
                } else if (matchesInRound === 8) {
                  roundName = '1/8 финала';
                } else {
                  roundName = `1/${matchesInRound * 2} финала`;
                }
                
                rounds.push({
                  round: i,
                  name: roundName,
                  matches: matchesInRound
                });
              }
            }
          }

          console.log('Playoff stage setup:', {
            qualifiedFromGroups: playoffStageData.qualifiedFromGroups,
            groupsCount,
            calculatedPlayerCount,
            finalPlayerCount,
            directFinal: playoffStageData.directFinal,
            thirdPlaceMatch: playoffStageData.thirdPlaceMatch,
            rounds
          });
          
          setPlayoffStage({
            qualifiedFromGroups: playoffStageData.qualifiedFromGroups || 0,
            playerCount: finalPlayerCount,
            rounds,
            directFinal: playoffStageData.directFinal || false,
            thirdPlaceMatch: playoffStageData.thirdPlaceMatch || false
          });
        } else {
          setPlayoffStage(null);
        }
      } else {
        setPlayoffStage(null);
      }
    } catch (e) {
      console.error('Ошибка парсинга структуры плей-офф:', e);
      setPlayoffStage(null);
    }
  };

  const handleDragStart = (e: React.DragEvent, player: Player) => {
    setDraggedPlayer(player);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', player.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, position: string) => {
    e.preventDefault();
    if (!draggedPlayer || !selectedTournament || !selectedLevel) return;

    const isDoubles = selectedTournament.format === 'DOUBLES';
    const isTeam = draggedPlayer.isTeam === true;

    // Проверяем, есть ли уже игрок/команда в этой позиции
    const existingEntryIndex = drawEntries.findIndex(entry => entry.position === position);
    
    if (existingEntryIndex >= 0) {
      // Обновляем существующую запись
      const updatedEntries = [...drawEntries];
      if (isDoubles && isTeam) {
        // Для парных турниров сохраняем команду
        updatedEntries[existingEntryIndex] = {
          ...updatedEntries[existingEntryIndex],
          playerId: undefined,
          player1Id: draggedPlayer.player1Id,
          player2Id: draggedPlayer.player2Id,
          player: undefined,
          player1: draggedPlayer.player1,
          player2: draggedPlayer.player2
        };
      } else {
        // Для одиночных турниров сохраняем игрока
        // Извлекаем оригинальный playerId из составного id (playerId-membershipId)
        const actualPlayerId = draggedPlayer.playerId || draggedPlayer.id.split('-')[0];
        updatedEntries[existingEntryIndex] = {
          ...updatedEntries[existingEntryIndex],
          playerId: actualPlayerId,
          player1Id: undefined,
          player2Id: undefined,
          player: draggedPlayer,
          player1: undefined,
          player2: undefined
        };
      }
      setDrawEntries(updatedEntries);
    } else {
      // Создаём новую запись
      // Для одиночных турниров извлекаем оригинальный playerId из составного id
      const actualPlayerId = (isDoubles && isTeam) 
        ? undefined 
        : (draggedPlayer.playerId || draggedPlayer.id.split('-')[0]);
      
      const newEntry: DrawEntry = {
        id: `temp-${Date.now()}`,
        position,
        playerId: actualPlayerId,
        player1Id: isDoubles && isTeam ? draggedPlayer.player1Id : undefined,
        player2Id: isDoubles && isTeam ? draggedPlayer.player2Id : undefined,
        bracketTemplateId: bracketTemplate?.id || undefined,
        groupId: undefined,
        player: isDoubles && isTeam ? undefined : draggedPlayer,
        player1: isDoubles && isTeam ? draggedPlayer.player1 : undefined,
        player2: isDoubles && isTeam ? draggedPlayer.player2 : undefined
      };
      
      setDrawEntries([...drawEntries, newEntry]);
    }

    setDraggedPlayer(null);
    setDraggedPosition(null);
  };

  const handleSave = async () => {
    if (!selectedTournament || !selectedLevel || !bracketTemplate) return;
    
    try {
      setSaving(true);
      
      // Определяем структуру групп для привязки groupId
      const structure = getGroupStructure();
      const positionsByGroup = getPositionsByGroup();
      
      // Создаём маппинг позиции -> groupIndex
      const positionToGroupIndex = new Map<string, number>();
      positionsByGroup.forEach(({ groupIndex, positions }) => {
        positions.forEach(pos => {
          positionToGroupIndex.set(pos, groupIndex);
        });
      });
      
      const entries = drawEntries.map(entry => {
        const groupIndex = positionToGroupIndex.get(entry.position);
        // Извлекаем membershipId из player.id, если он составной (playerId-membershipId)
        const player = entry.player;
        const membershipId = player?.membershipId || (player?.id?.includes('-') ? player.id.split('-')[1] : null);
        // Для позиций плей-офф убеждаемся, что bracketTemplateId установлен
        const isPlayoffPosition = entry.position.startsWith('playoff-');
        const bracketTemplateId = entry.bracketTemplateId || (isPlayoffPosition && bracketTemplate?.id ? bracketTemplate.id : null);
        return {
          position: entry.position,
          playerId: entry.playerId || null,
          membershipId: membershipId || null, // ID регистрации для различения нескольких регистраций
          player1Id: entry.player1Id || null,
          player2Id: entry.player2Id || null,
          seed: entry.seed || null,
          bracketTemplateId: bracketTemplateId,
          groupIndex: groupIndex !== undefined ? groupIndex : null,
          groupPlace: entry.groupPlace || null // Сохраняем место из группы для плей-офф
        };
      });

      await apiPost(`/draws/tournament/${selectedTournament.id}/level/${selectedLevel}`, { 
        entries,
        groupNames: groupNames // Отправляем названия групп
      });
      
      alert('Жеребьёвка успешно сохранена');
      await fetchDraw();
      await fetchPlayoffStage(); // Перезагружаем структуру плей-офф после сохранения
    } catch (error: any) {
      console.error('Ошибка сохранения:', error);
      alert(`Ошибка сохранения: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      setSaving(false);
    }
  };

  const getPlayerAtPosition = (position: string): Player | null => {
    const entry = drawEntries.find(e => e.position === position);
    if (!entry) return null;
    
    // Для парных турниров проверяем команду
    if (selectedTournament?.format === 'DOUBLES') {
      if (entry.player1 && entry.player2) {
        // Формируем объект команды
        return {
          id: entry.player1Id && entry.player2Id ? `${entry.player1Id}-${entry.player2Id}` : 'temp',
          name: `${entry.player1.name} / ${entry.player2.name}`,
          isTeam: true,
          player1Id: entry.player1Id,
          player2Id: entry.player2Id,
          player1: entry.player1,
          player2: entry.player2
        };
      }
      // Если есть player1Id и player2Id, но нет объектов, ищем в списке игроков
      if (entry.player1Id && entry.player2Id) {
        const team = players.find(p => 
          p.isTeam && p.player1Id === entry.player1Id && p.player2Id === entry.player2Id
        );
        if (team) return team;
      }
    } else {
      // Для одиночных турниров
      if (entry.player) return entry.player;
      if (entry.playerId) {
        // Ищем игрока по playerId (может быть составной id playerId-membershipId)
        // Сначала ищем точное совпадение, потом по playerId
        const found = players.find(p => {
          const pPlayerId = p.playerId || p.id.split('-')[0];
          return p.id === entry.playerId || pPlayerId === entry.playerId;
        });
        return found || null;
      }
    }
    return null;
  };

  const removePlayerFromPosition = (position: string) => {
    setDrawEntries(drawEntries.filter(e => e.position !== position));
  };

  const getGroupEntries = (groupIndex: number): DrawEntry[] => {
    const groups = getPositionsByGroup();
    const target = groups.find((g) => g.groupIndex === groupIndex);
    if (!target) return [];
    const posSet = new Set(target.positions);
    return drawEntries.filter((entry) => posSet.has(entry.position));
  };

  const getPlayerNameById = (playerId: string): string => {
    const byPool = players.find((p) => {
      const actualId = p.playerId || p.id.split('-')[0];
      return actualId === playerId;
    });
    if (byPool?.name) return byPool.name;
    const byEntry = drawEntries.find((e) => e.playerId === playerId);
    if (byEntry?.player?.name) return byEntry.player.name;
    return playerId;
  };

  const openReplaceMemberModal = async (groupIndex: number) => {
    if (!selectedTournament || !selectedLevel) return;
    const groupId = groupIdsByIndex[groupIndex];
    if (!groupId) {
      alert('Сначала сохраните жеребьёвку группы, чтобы выполнить замену.');
      return;
    }

    const groupEntries = getGroupEntries(groupIndex);
    const groupPlayerIds = Array.from(new Set(groupEntries.map((e) => e.playerId).filter(Boolean) as string[]));
    if (groupPlayerIds.length === 0) {
      alert('В этой группе нет назначенных игроков для замены.');
      return;
    }

    setReplacementGroupIndex(groupIndex);
    setReplacementFromPlayerId(groupPlayerIds[0]);
    setReplacementToPlayerId('');
    setReplacementMode('STANDARD');
    setReplacementCandidates([]);

    try {
      const memberships = await apiGet<LevelMembership[]>(
        `/tournaments/${selectedTournament.id}/players?level=${selectedLevel}`
      );
      const unique = new Map<string, { id: string; name: string }>();
      (Array.isArray(memberships) ? memberships : []).forEach((membership) => {
        const id = membership.player?.id || membership.playerId;
        const name = membership.player?.name || '';
        if (!id || !name) return;
        if (groupPlayerIds.includes(id)) return;
        if (!unique.has(id)) unique.set(id, { id, name });
      });
      const candidates = Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      setReplacementCandidates(candidates);
      if (candidates.length > 0) {
        setReplacementToPlayerId(candidates[0].id);
      }
    } catch (error) {
      console.error('Ошибка загрузки кандидатов для замены:', error);
      setReplacementCandidates([]);
    }
  };

  const closeReplaceMemberModal = () => {
    setReplacementGroupIndex(null);
    setReplacementFromPlayerId('');
    setReplacementToPlayerId('');
    setReplacementMode('STANDARD');
    setReplacementCandidates([]);
  };

  const handleReplaceMember = async () => {
    if (replacementGroupIndex === null || !selectedTournament || !selectedLevel) return;
    const groupId = groupIdsByIndex[replacementGroupIndex];
    if (!groupId) {
      alert('Не удалось определить группу. Сохраните жеребьёвку и повторите.');
      return;
    }
    if (!replacementFromPlayerId || !replacementToPlayerId) {
      alert('Выберите обоих игроков для замены.');
      return;
    }
    if (replacementFromPlayerId === replacementToPlayerId) {
      alert('Игрок для замены должен отличаться от текущего участника.');
      return;
    }

    try {
      setReplacingMember(true);
      const result = await apiPost<{
        updatedMatches?: number;
        updatedDrawEntries?: number;
        archivedCompletedMatches?: number;
        createdReplayMatches?: number;
      }>(
        `/tournament-groups/${groupId}/replace-member`,
        {
          fromPlayerId: replacementFromPlayerId,
          toPlayerId: replacementToPlayerId,
          replaceMode: replacementMode
        }
      );
      const updatedMatches = result?.updatedMatches ?? 0;
      const updatedDrawEntries = result?.updatedDrawEntries ?? 0;
      const archivedCompletedMatches = result?.archivedCompletedMatches ?? 0;
      const createdReplayMatches = result?.createdReplayMatches ?? 0;
      const replayPart = replacementMode === 'REPLAY_ALL_GAMES'
        ? `, убрано из группы сыгранных матчей: ${archivedCompletedMatches}, создано новых матчей: ${createdReplayMatches}`
        : '';
      alert(`Замена выполнена. Обновлено несыгранных матчей: ${updatedMatches}, слотов в сетке: ${updatedDrawEntries}${replayPart}`);
      closeReplaceMemberModal();
      await fetchDraw();
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Неизвестная ошибка';
      alert(`Ошибка замены участника: ${message}`);
    } finally {
      setReplacingMember(false);
    }
  };

  // Определяет структуру групп из шаблона
  const getGroupStructure = (): { groups: number; playersPerGroup: number } | null => {
    if (!bracketTemplate) return null;
    
    try {
      const structure = typeof bracketTemplate.structure === 'string' 
        ? JSON.parse(bracketTemplate.structure)
        : bracketTemplate.structure;
      
      // Для Mixed формата ищем первый Round Robin этап
      if (bracketTemplate.format === 'MIXED' && structure.stages) {
        const roundRobinStage = structure.stages.find((s: any) => s.type === 'ROUND_ROBIN');
        if (roundRobinStage) {
          return {
            groups: roundRobinStage.groups || 1,
            playersPerGroup: roundRobinStage.playersPerGroup || bracketTemplate.playerCount
          };
        }
      }
      
      // Для Round Robin формата
      if (bracketTemplate.format === 'ROUND_ROBIN') {
        // По умолчанию одна группа со всеми игроками
        return {
          groups: 1,
          playersPerGroup: bracketTemplate.playerCount
        };
      }
      
      // Для Single Elimination - без групп
      return null;
    } catch (e) {
      console.error('Ошибка парсинга структуры:', e);
      return null;
    }
  };

  const generateBracketPositions = (): string[] => {
    if (!bracketTemplate) return [];
    
    const positions: string[] = [];
    for (let i = 1; i <= bracketTemplate.playerCount; i++) {
      positions.push(i.toString());
    }
    return positions;
  };

  const templatePositionServiceCodes = useMemo<Record<string, string>>(() => {
    if (!bracketTemplate?.structure) return {};
    try {
      const parsedStructure =
        typeof bracketTemplate.structure === 'string'
          ? JSON.parse(bracketTemplate.structure)
          : bracketTemplate.structure;
      const rawCodes = parsedStructure?.positionServiceCodes;
      if (!rawCodes || typeof rawCodes !== 'object') return {};
      const normalized: Record<string, string> = {};
      Object.entries(rawCodes as Record<string, unknown>).forEach(([key, value]) => {
        if (typeof value !== 'string') return;
        const trimmed = value.trim();
        if (!trimmed) return;
        normalized[String(key)] = trimmed;
      });
      return normalized;
    } catch {
      return {};
    }
  }, [bracketTemplate?.structure]);

  const getPositionServiceCode = (position: string): string | null => {
    const byExactKey = templatePositionServiceCodes[position];
    if (byExactKey) return byExactKey;
    const numericPosition = position.startsWith('playoff-') ? position.replace('playoff-', '') : position;
    return templatePositionServiceCodes[numericPosition] || null;
  };

  const getPositionLabel = (position: string): string => {
    const code = getPositionServiceCode(position);
    if (code) {
      return `Позиция ${position} - ${code}`;
    }
    return `Позиция ${position}`;
  };

  // Разбивает позиции по группам
  const getPositionsByGroup = (): Array<{ groupIndex: number; positions: string[] }> => {
    const structure = getGroupStructure();
    if (!structure) {
      // Если нет групп, возвращаем все позиции как одну группу
      return [{ groupIndex: 0, positions: generateBracketPositions() }];
    }
    
    const { groups, playersPerGroup } = structure;
    const allPositions = generateBracketPositions();
    const result: Array<{ groupIndex: number; positions: string[] }> = [];
    
    for (let groupIndex = 0; groupIndex < groups; groupIndex++) {
      const startIndex = groupIndex * playersPerGroup;
      const endIndex = startIndex + playersPerGroup;
      const groupPositions = allPositions.slice(startIndex, endIndex);
      result.push({ groupIndex, positions: groupPositions });
    }
    
    return result;
  };
  const hasGroupStage = getGroupStructure() !== null;

  const unassignedPlayers = players.filter(player => {
    if (selectedTournament?.format === 'DOUBLES' && player.isTeam) {
      // Для парных турниров проверяем команды
      return !drawEntries.some(entry => 
        entry.player1Id === player.player1Id && entry.player2Id === player.player2Id
      );
    } else {
      // Для одиночных турниров: показываем только незанятые регистрации
      const actualPlayerId = player.playerId || player.id.split('-')[0];
      const membershipId = player.membershipId || (player.id.includes('-') ? player.id.split('-')[1] : null);
      
      // Подсчитываем, сколько раз этот игрок уже назначен в сетку
      const assignedCount = drawEntries.filter(entry => entry.playerId === actualPlayerId).length;
      
      // Подсчитываем, сколько всего регистраций у этого игрока
      const allPlayerRegistrations = players.filter(p => {
        const pPlayerId = p.playerId || p.id.split('-')[0];
        return pPlayerId === actualPlayerId;
      });
      const totalRegistrations = allPlayerRegistrations.length;
      
      // Если игрок уже назначен столько раз, сколько у него регистраций, скрываем все его регистрации
      if (assignedCount >= totalRegistrations) {
        return false;
      }
      
      // Если у игрока несколько регистраций, показываем только те, которые еще не назначены
      // Проблема: мы не можем точно определить, какая именно регистрация назначена,
      // потому что DrawEntry не хранит membershipId. Поэтому используем упрощенную логику:
      // показываем только (totalRegistrations - assignedCount) регистраций
      // Для этого берем только первые незанятые регистрации
      if (totalRegistrations > 1) {
        // Находим индекс текущей регистрации среди всех регистраций игрока
        const currentRegistrationIndex = allPlayerRegistrations.findIndex(p => p.id === player.id);
        // Показываем только первые (totalRegistrations - assignedCount) незанятые регистрации
        // Это означает, что мы показываем регистрации с индексами от 0 до (totalRegistrations - assignedCount - 1)
        const maxUnassignedIndex = totalRegistrations - assignedCount - 1;
        return currentRegistrationIndex <= maxUnassignedIndex;
      } else {
        // Для одной регистрации: показываем только если не назначена
        return assignedCount === 0;
      }
    }
  });

  // Генерирует список мест из групп для плей-офф (A-1, A-2, B-1, B-2 и т.д.)
  // Исключает уже использованные места
  const generateGroupPlaces = (): string[] => {
    if (!playoffStage || playoffStage.qualifiedFromGroups === 0) return [];
    
    const structure = getGroupStructure();
    if (!structure) return [];
    
    // Получаем список уже использованных мест из групп
    const usedPlaces = new Set(
      drawEntries
        .filter(entry => entry.groupPlace && entry.position.startsWith('playoff-'))
        .map(entry => entry.groupPlace!)
    );
    
    const places: string[] = [];
    for (let groupIndex = 0; groupIndex < structure.groups; groupIndex++) {
      const groupLetter = String.fromCharCode(65 + groupIndex); // A, B, C...
      for (let place = 1; place <= playoffStage.qualifiedFromGroups; place++) {
        const placeKey = `${groupLetter}-${place}`;
        // Добавляем только если место еще не использовано
        if (!usedPlaces.has(placeKey)) {
          places.push(placeKey);
        }
      }
    }
    return places;
  };

  // Получает место из группы для позиции плей-офф
  const getGroupPlaceAtPlayoffPosition = (position: string): string | null => {
    const entry = drawEntries.find(e => e.position === position && e.position.startsWith('playoff-'));
    return entry?.groupPlace || null;
  };

  // Генерирует позиции для плей-офф
  const generatePlayoffPositions = (): string[] => {
    if (!playoffStage || playoffStage.rounds.length === 0) return [];
    
    // Для режима directFinal генерируем позиции для всех раундов
    if (playoffStage.directFinal) {
      const positions: string[] = [];
      
      // Сортируем раунды: сначала Финал, потом Матч за 3-е место
      // Это нужно, чтобы позиции 1-2 были для финала, а 3-4 для матча за 3-е место
      const sortedRounds = [...playoffStage.rounds].sort((a, b) => {
        if (a.name === 'Финал' || a.name === 'Finala') return -1;
        if (b.name === 'Финал' || b.name === 'Finala') return 1;
        return 0;
      });
      
      let positionIndex = 1;
      
      // Для каждого раунда генерируем позиции
      sortedRounds.forEach(round => {
        for (let i = 0; i < round.matches * 2; i++) {
          positions.push(`playoff-${positionIndex}`);
          positionIndex++;
        }
      });
      
      console.log('Playoff positions generated (directFinal):', {
        positions,
        rounds: sortedRounds,
        thirdPlaceMatch: playoffStage.thirdPlaceMatch
      });
      
      return positions;
    }
    
    // Классический плей-офф - генерируем позиции для первого раунда
    const firstRound = playoffStage.rounds.reduce((max, round) => 
      round.round > max.round ? round : max
    );
    
    if (!firstRound) return [];
    
    // Для каждого матча нужно 2 позиции (по одному игроку с каждой стороны)
    const positions: string[] = [];
    const totalPositions = firstRound.matches * 2;
    
    for (let i = 1; i <= totalPositions; i++) {
      positions.push(`playoff-${i}`);
    }
    
    console.log('Playoff positions generated:', {
      round: firstRound.name,
      roundNumber: firstRound.round,
      matches: firstRound.matches,
      totalPositions,
      positions,
      allRounds: playoffStage.rounds
    });
    
    return positions;
  };

  // Обработчик перетаскивания места из группы
  const handleGroupPlaceDragStart = (e: React.DragEvent, groupPlace: string) => {
    setDraggedGroupPlace(groupPlace);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', groupPlace);
  };

  // Обработчик drop для плей-офф позиций
  const handlePlayoffDrop = (e: React.DragEvent, position: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedGroupPlace) {
      // Удаляем старую запись для этой позиции
      const filteredEntries = drawEntries.filter(e => e.position !== position);
      
      // Добавляем новую запись
      const newEntry: DrawEntry = {
        id: `temp-${Date.now()}`,
        position,
        groupPlace: draggedGroupPlace,
        bracketTemplateId: bracketTemplate?.id || undefined
      };
      
      setDrawEntries([...filteredEntries, newEntry]);
    }
    
    setDraggedGroupPlace(null);
  };

  return (
    <div className="p-4 h-full overflow-hidden">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Жеребьёвка</h1>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Турнир
            </label>
            <select
              value={selectedTournament?.id || ''}
              onChange={(e) => {
                const tournament = tournaments.find(t => t.id === e.target.value);
                setSelectedTournament(tournament || null);
                setSelectedLevel('');
                setDrawEntries([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Выберите турнир</option>
              {tournamentsGrouped.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Уровень
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setDrawEntries([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={!selectedTournament}
            >
              <option value="">Выберите уровень</option>
              {levels.map(l => (
                <option key={l.level} value={l.level}>
                  {l.level} ({l.playerCount} {selectedTournament?.format === 'DOUBLES' ? 'команд' : 'игроков'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {bracketTemplate && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Шаблон сетки:</strong> {bracketTemplate.name} ({bracketTemplate.format}, {bracketTemplate.playerCount} {selectedTournament?.format === 'DOUBLES' ? 'команд' : 'игроков'})
            </p>
          </div>
        )}
        {selectedTournament && selectedLevel && !bracketTemplate && (
          <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Внимание:</strong> Для уровня "{selectedLevel}" не настроен шаблон сетки. 
              Настройте шаблон в карточке турнира, чтобы начать жеребьёвку.
            </p>
          </div>
        )}
      </div>

      {selectedTournament && selectedLevel && (
        <div className="grid grid-cols-3 gap-6">
          {/* Список незанятых игроков/команд и мест из групп для плей-офф */}
          <div className="bg-white rounded-lg shadow p-4 h-[calc(100vh-320px)] min-h-[360px] flex flex-col">
            <h2 className="text-lg font-semibold mb-4">
              {selectedTournament?.format === 'DOUBLES' ? 'Незанятые команды' : 'Незанятые игроки'}
            </h2>
            <div className="space-y-2 overflow-y-auto mb-4 flex-1 min-h-0">
              {unassignedPlayers.map((player, index) => {
                // Определяем, сколько всего регистраций у этого игрока
                const actualPlayerId = player.playerId || player.id.split('-')[0];
                const allPlayerRegistrations = players.filter(p => {
                  const pPlayerId = p.playerId || p.id.split('-')[0];
                  return pPlayerId === actualPlayerId;
                });
                const registrationNumber = allPlayerRegistrations.findIndex(p => p.id === player.id) + 1;
                const totalRegistrations = allPlayerRegistrations.length;
                
                // Определяем, сколько раз этот игрок уже назначен и в какие группы
                const assignedEntries = drawEntries.filter(entry => entry.playerId === actualPlayerId);
                const assignedCount = assignedEntries.length;
                
                // Определяем, в какие группы уже назначен игрок
                const assignedGroups: string[] = [];
                assignedEntries.forEach(entry => {
                  if (entry.group?.name) {
                    assignedGroups.push(entry.group.name);
                  } else {
                    // Пытаемся определить группу по позиции
                    const structure = getGroupStructure();
                    if (structure) {
                      const positionNum = parseInt(entry.position);
                      if (!isNaN(positionNum)) {
                        const groupIndex = Math.floor((positionNum - 1) / structure.playersPerGroup);
                        const groupName = groupNames[groupIndex] || `Группа ${String.fromCharCode(65 + groupIndex)}`;
                        if (!assignedGroups.includes(groupName)) {
                          assignedGroups.push(groupName);
                        }
                      }
                    }
                  }
                });
                
                // Показываем информацию о регистрации, если их несколько
                const showRegistrationInfo = totalRegistrations > 1;
                
                return (
                  <div
                    key={player.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, player)}
                    className="py-1.5 px-2.5 bg-gray-100 rounded cursor-move hover:bg-gray-200 border border-gray-300 text-sm flex items-center justify-between"
                    title={showRegistrationInfo 
                      ? `Регистрация ${registrationNumber} из ${totalRegistrations}. Назначено: ${assignedCount}/${totalRegistrations}${assignedGroups.length > 0 ? `. Уже в группах: ${assignedGroups.join(', ')}` : ''}` 
                      : undefined}
                  >
                    <span>{player.name}</span>
                    {showRegistrationInfo && (
                      <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        #{registrationNumber}
                        {assignedCount > 0 && (
                          <span className="ml-1 text-gray-500">
                            ({assignedCount}/{totalRegistrations})
                            {assignedGroups.length > 0 && (
                              <span className="ml-1">в {assignedGroups.join(', ')}</span>
                            )}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
              {unassignedPlayers.length === 0 && (
                <p className="text-sm text-gray-500">
                  {selectedTournament?.format === 'DOUBLES' ? 'Все команды распределены' : 'Все игроки распределены'}
                </p>
              )}
            </div>

            {/* Места из групп для плей-офф */}
            {playoffStage && playoffStage.qualifiedFromGroups > 0 && (() => {
              const availablePlaces = generateGroupPlaces();
              return (
                <>
                  <h2 className="text-lg font-semibold mb-4 text-blue-700">Места из групп для плей-офф</h2>
                  <div className="space-y-2 overflow-y-auto max-h-[35%] min-h-[120px]">
                    {availablePlaces.length > 0 ? (
                      availablePlaces.map(groupPlace => (
                        <div
                          key={groupPlace}
                          draggable
                          onDragStart={(e) => handleGroupPlaceDragStart(e, groupPlace)}
                          className="py-1.5 px-2.5 bg-blue-100 rounded cursor-move hover:bg-blue-200 border border-blue-300 text-sm font-semibold text-blue-800"
                        >
                          {groupPlace}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">Все места распределены</p>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Сетка жеребьёвки */}
          <div className="col-span-2 bg-white rounded-lg shadow p-4 h-[calc(100vh-320px)] min-h-[360px] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Сетка</h2>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>

            <div className="overflow-y-auto space-y-6 flex-1 min-h-0">
              {getPositionsByGroup().map(({ groupIndex, positions }) => (
                <div key={groupIndex} className="space-y-2">
                  {/* Поле ввода названия группы */}
                  {hasGroupStage && (
                    <div className="mb-2 flex items-center gap-2">
                      <input
                        type="text"
                        value={groupNames[groupIndex] || ''}
                        onChange={(e) => {
                          setGroupNames({ ...groupNames, [groupIndex]: e.target.value });
                        }}
                        placeholder={`Группа ${String.fromCharCode(65 + groupIndex)}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {selectedTournament?.format !== 'DOUBLES' && (
                        <button
                          type="button"
                          onClick={() => openReplaceMemberModal(groupIndex)}
                          className="shrink-0 px-3 py-2 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                          title="Заменить игрока в группе"
                        >
                          Замена
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Позиции в группе */}
                  <div className="grid grid-cols-1 gap-2">
                    {positions.map(position => {
                      const player = getPlayerAtPosition(position);
                      return (
                        <div
                          key={position}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, position)}
                          className={`py-0.5 px-1.5 border-2 border-dashed rounded min-h-[32px] flex flex-col ${
                            player
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-300 bg-gray-50 hover:border-blue-400'
                          }`}
                        >
                          <div className="flex justify-between items-center mb-0">
                            <span className="text-[9px] font-semibold text-gray-500 leading-none">{getPositionLabel(position)}</span>
                            {player && (
                              <button
                                onClick={() => removePlayerFromPosition(position)}
                                className="text-red-500 hover:text-red-700 text-[10px] leading-none p-0 m-0 h-3 w-3 flex items-center justify-center"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          {player ? (
                            <div className="font-medium text-gray-900 text-xs leading-tight mt-0.5">{player.name}</div>
                          ) : (
                            <div className="text-[9px] text-gray-400 italic leading-tight mt-0.5">
                              {selectedTournament?.format === 'DOUBLES' ? 'Перетащите команду сюда' : 'Перетащите игрока сюда'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Плей-офф этап */}
              {playoffStage && playoffStage.qualifiedFromGroups > 0 && (
                <div className="space-y-4 border-t-2 border-blue-300 pt-4 mt-6">
                  <h3 className="text-lg font-semibold text-blue-700">Плей-офф (на вылет)</h3>
                  
                  {/* Раунды плей-офф */}
                  {playoffStage.rounds.length > 0 && (() => {
                    const positions = generatePlayoffPositions();
                    
                    // Для режима directFinal показываем раунды отдельно с их названиями
                    if (playoffStage.directFinal) {
                      // Сортируем раунды: Финал слева, Матч за 3-е место справа
                      const sortedRounds = [...playoffStage.rounds].sort((a, b) => {
                        if (a.name === 'Финал' || a.name === 'Finala') return -1;
                        if (b.name === 'Финал' || b.name === 'Finala') return 1;
                        return 0;
                      });
                      
                      return (
                        <div className="flex gap-6">
                          {sortedRounds.map((round, roundIndex) => {
                            const matchesInRound = round.matches;
                            
                            // Определяем стартовый индекс позиций в зависимости от названия раунда
                            let startPositionIndex = 0;
                            if (round.name === 'Матч за 3-е место') {
                              // Для матча за 3-е место используем позиции 3 и 4 (индексы 2 и 3)
                              startPositionIndex = 2;
                            } else if (round.name === 'Финал' || round.name === 'Finala') {
                              // Для финала используем позиции 1 и 2 (индексы 0 и 1)
                              startPositionIndex = 0;
                            }
                            
                            const matches: Array<{ position1: string; position2: string }> = [];
                            for (let i = 0; i < matchesInRound; i++) {
                              const pos1Index = startPositionIndex + i * 2;
                              const pos2Index = startPositionIndex + i * 2 + 1;
                              if (pos1Index < positions.length && pos2Index < positions.length) {
                                matches.push({
                                  position1: positions[pos1Index],
                                  position2: positions[pos2Index]
                                });
                              }
                            }
                            
                            // Не отображаем раунд, если для него нет матчей
                            if (matches.length === 0) {
                              return null;
                            }
                            
                            return (
                              <div key={roundIndex} className="flex-1 space-y-2">
                                <h4 className="text-sm font-semibold text-gray-700 text-center">
                                  {round.name}
                                </h4>
                                <div className="flex flex-col gap-4">
                                  {matches.map((match, matchIndex) => {
                                    const groupPlace1 = getGroupPlaceAtPlayoffPosition(match.position1);
                                    const groupPlace2 = getGroupPlaceAtPlayoffPosition(match.position2);
                                    
                                    return (
                                      <div key={matchIndex} className="border-2 border-purple-300 rounded-lg p-3 bg-purple-50">
                                        <div className="text-xs font-bold text-purple-700 mb-2 text-center">
                                          Матч {matchIndex + 1}
                                        </div>
                                        <div className="space-y-2">
                                          {/* Позиция 1 матча */}
                                          <div
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handlePlayoffDrop(e, match.position1)}
                                            className={`py-1.5 px-2 border-2 border-dashed rounded min-h-[40px] flex flex-col ${
                                              groupPlace1
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-300 bg-white hover:border-blue-400'
                                            }`}
                                          >
                                            <div className="flex justify-between items-center mb-0">
                                              <span className="text-[10px] font-semibold text-gray-600 leading-none">
                                                Позиция {match.position1.replace('playoff-', '')}
                                              </span>
                                              {groupPlace1 && (
                                                <button
                                                  onClick={() => {
                                                    setDrawEntries(drawEntries.filter(e => e.position !== match.position1));
                                                  }}
                                                  className="text-red-500 hover:text-red-700 text-xs leading-none p-0.5 m-0 h-4 w-4 flex items-center justify-center"
                                                >
                                                  ✕
                                                </button>
                                              )}
                                            </div>
                                            {groupPlace1 ? (
                                              <div className="font-bold text-blue-900 text-sm leading-tight mt-1">
                                                {groupPlace1}
                                              </div>
                                            ) : (
                                              <div className="text-[10px] text-gray-400 italic leading-tight mt-1">
                                                Перетащите место из группы
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* VS разделитель */}
                                          <div className="text-center text-sm font-bold text-purple-600 py-1">VS</div>
                                          
                                          {/* Позиция 2 матча */}
                                          <div
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handlePlayoffDrop(e, match.position2)}
                                            className={`py-1.5 px-2 border-2 border-dashed rounded min-h-[40px] flex flex-col ${
                                              groupPlace2
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-300 bg-white hover:border-blue-400'
                                            }`}
                                          >
                                            <div className="flex justify-between items-center mb-0">
                                              <span className="text-[10px] font-semibold text-gray-600 leading-none">
                                                Позиция {match.position2.replace('playoff-', '')}
                                              </span>
                                              {groupPlace2 && (
                                                <button
                                                  onClick={() => {
                                                    setDrawEntries(drawEntries.filter(e => e.position !== match.position2));
                                                  }}
                                                  className="text-red-500 hover:text-red-700 text-xs leading-none p-0.5 m-0 h-4 w-4 flex items-center justify-center"
                                                >
                                                  ✕
                                                </button>
                                              )}
                                            </div>
                                            {groupPlace2 ? (
                                              <div className="font-bold text-blue-900 text-sm leading-tight mt-1">
                                                {groupPlace2}
                                              </div>
                                            ) : (
                                              <div className="text-[10px] text-gray-400 italic leading-tight mt-1">
                                                Перетащите место из группы
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                    
                    // Классический плей-офф - показываем первый раунд
                    const firstRound = playoffStage.rounds.reduce((max, round) => 
                      round.round > max.round ? round : max
                    );
                    
                    const matchesInRound = firstRound?.matches || 1;
                    
                    // Группируем позиции по матчам (крест на крест)
                    // Для полуфинала: матч 1 = позиции 1 и 2, матч 2 = позиции 3 и 4
                    // Для 1/4 финала: матч 1 = позиции 1 и 2, матч 2 = позиции 3 и 4, и т.д.
                    const matches: Array<{ position1: string; position2: string }> = [];
                    for (let i = 0; i < matchesInRound; i++) {
                      const pos1Index = i * 2;
                      const pos2Index = i * 2 + 1;
                      if (pos1Index < positions.length && pos2Index < positions.length) {
                        matches.push({
                          position1: positions[pos1Index],
                          position2: positions[pos2Index]
                        });
                      }
                    }
                    
                    return (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700">
                          {firstRound?.name || 'Первый раунд'}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {matches.map((match, matchIndex) => {
                            const groupPlace1 = getGroupPlaceAtPlayoffPosition(match.position1);
                            const groupPlace2 = getGroupPlaceAtPlayoffPosition(match.position2);
                            
                            return (
                              <div key={matchIndex} className="border-2 border-purple-300 rounded-lg p-3 bg-purple-50">
                                <div className="text-xs font-bold text-purple-700 mb-2 text-center">
                                  Матч {matchIndex + 1}
                                </div>
                                <div className="space-y-2">
                                  {/* Позиция 1 матча */}
                                  <div
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handlePlayoffDrop(e, match.position1)}
                                    className={`py-1.5 px-2 border-2 border-dashed rounded min-h-[40px] flex flex-col ${
                                      groupPlace1
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300 bg-white hover:border-blue-400'
                                    }`}
                                  >
                                    <div className="flex justify-between items-center mb-0">
                                      <span className="text-[10px] font-semibold text-gray-600 leading-none">
                                        Позиция {match.position1.replace('playoff-', '')}
                                      </span>
                                      {groupPlace1 && (
                                        <button
                                          onClick={() => {
                                            setDrawEntries(drawEntries.filter(e => e.position !== match.position1));
                                          }}
                                          className="text-red-500 hover:text-red-700 text-xs leading-none p-0.5 m-0 h-4 w-4 flex items-center justify-center"
                                        >
                                          ✕
                                        </button>
                                      )}
                                    </div>
                                    {groupPlace1 ? (
                                      <div className="font-bold text-blue-900 text-sm leading-tight mt-1">
                                        {groupPlace1}
                                      </div>
                                    ) : (
                                      <div className="text-[10px] text-gray-400 italic leading-tight mt-1">
                                        Перетащите место из группы
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* VS разделитель */}
                                  <div className="text-center text-sm font-bold text-purple-600 py-1">VS</div>
                                  
                                  {/* Позиция 2 матча */}
                                  <div
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handlePlayoffDrop(e, match.position2)}
                                    className={`py-1.5 px-2 border-2 border-dashed rounded min-h-[40px] flex flex-col ${
                                      groupPlace2
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300 bg-white hover:border-blue-400'
                                    }`}
                                  >
                                    <div className="flex justify-between items-center mb-0">
                                      <span className="text-[10px] font-semibold text-gray-600 leading-none">
                                        Позиция {match.position2.replace('playoff-', '')}
                                      </span>
                                      {groupPlace2 && (
                                        <button
                                          onClick={() => {
                                            setDrawEntries(drawEntries.filter(e => e.position !== match.position2));
                                          }}
                                          className="text-red-500 hover:text-red-700 text-xs leading-none p-0.5 m-0 h-4 w-4 flex items-center justify-center"
                                        >
                                          ✕
                                        </button>
                                      )}
                                    </div>
                                    {groupPlace2 ? (
                                      <div className="font-bold text-blue-900 text-sm leading-tight mt-1">
                                        {groupPlace2}
                                      </div>
                                    ) : (
                                      <div className="text-[10px] text-gray-400 italic leading-tight mt-1">
                                        Перетащите место из группы
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && tournaments.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Загрузка турниров...</p>
        </div>
      )}

      {!loading && tournaments.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Турниры не найдены</p>
        </div>
      )}

      {!selectedTournament && !loading && tournaments.length > 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Выберите турнир и уровень для начала жеребьёвки</p>
        </div>
      )}

      {replacementGroupIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                Замена игрока в группе {groupNames[replacementGroupIndex] || `Группа ${String.fromCharCode(65 + replacementGroupIndex)}`}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {replacementMode === 'REPLAY_ALL_GAMES'
                  ? 'Сыгранные матчи заменяемого игрока будут убраны из группы и сохранены в истории. Для нового игрока создадутся новые несыгранные матчи.'
                  : 'Будут обновлены только несыгранные матчи группы. Сыгранные матчи останутся без изменений.'}
              </p>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Кого заменить</label>
                <select
                  value={replacementFromPlayerId}
                  onChange={(e) => setReplacementFromPlayerId(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Выберите игрока</option>
                  {Array.from(
                    new Set(getGroupEntries(replacementGroupIndex).map((entry) => entry.playerId).filter(Boolean) as string[])
                  ).map((playerId) => (
                    <option key={playerId} value={playerId}>
                      {getPlayerNameById(playerId)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Новый игрок</label>
                <select
                  value={replacementToPlayerId}
                  onChange={(e) => setReplacementToPlayerId(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  {replacementCandidates.length === 0 && (
                    <option value="">Нет доступных кандидатов на этом уровне</option>
                  )}
                  {replacementCandidates.map((player) => (
                    <option key={player.id} value={player.id}>
                      {player.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rounded border border-indigo-200 bg-indigo-50 px-3 py-2">
                <label className="flex items-start gap-2 text-sm text-indigo-900">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={replacementMode === 'REPLAY_ALL_GAMES'}
                    onChange={(e) => setReplacementMode(e.target.checked ? 'REPLAY_ALL_GAMES' : 'STANDARD')}
                  />
                  <span>
                    Режим «как будто не играл»: убрать сыгранные матчи заменяемого игрока из группы и создать новые матчи для нового игрока.
                  </span>
                </label>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeReplaceMemberModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={replacingMember}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleReplaceMember}
                disabled={replacingMember || !replacementFromPlayerId || !replacementToPlayerId}
                className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {replacingMember ? 'Замена...' : 'Подтвердить замену'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Draws;

